import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUserProjectsListComponent } from '@portal-core/license-users/components/license-user-projects-list/license-user-projects-list.component';
import { LicenseUserTeamsListComponent } from '@portal-core/license-users/components/license-user-teams-list/license-user-teams-list.component';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { ProjectStatus } from '@portal-core/projects/enums/project-status.enum';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { AutocompleteInputComponent } from '@portal-core/ui/autocomplete/components/autocomplete-input/autocomplete-input.component';
import { PageFilterService } from '@portal-core/ui/page-filters/services/page-filter.service';
import { PopupComponent } from '@portal-core/ui/popup/components/popup/popup.component';
import { PopupTriggerDirective } from '@portal-core/ui/popup/directives/popup-trigger/popup-trigger.directive';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { map, Observable, Subscription } from 'rxjs';

export enum UserAccessFormTab {
  Teams = 0,
  Projects
}

@Component({
  selector: 'mc-user-access-form',
  templateUrl: './user-access-form.component.html',
  styleUrls: ['./user-access-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAccessFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() selectedTabIndex?: UserAccessFormTab;
  @Input() licenseUser: LicenseUser;

  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(LicenseUserTeamsListComponent, { static: false }) licenseUserTeamsListComponent?: LicenseUserTeamsListComponent;
  @ViewChild('licenseUserTeamsAutoComplete', { static: false }) licenseUserTeamsAutocomplete: AutocompleteInputComponent;
  @ViewChild('popupTriggerTeams', { static: false, read: PopupTriggerDirective }) popupTriggerTeams: PopupTriggerDirective;
  @ViewChild('addUserTeamsPopup', { static: false }) addUserTeamsPopup: PopupComponent;
  @ViewChild('filterUserTeams', { static: false }) filterUserTeams: ElementRef;
  licenseUserTeamsFilterName: string = 'license-user-teams';
  filterUserTeamsSubscription: Subscription;

  @ViewChild(LicenseUserProjectsListComponent, { static: false }) licenseUserProjectsListComponent?: LicenseUserProjectsListComponent;
  @ViewChild('licenseUserProjectsAutoComplete', { static: false }) licenseUserProjectsAutocomplete: AutocompleteInputComponent;
  @ViewChild('popupTriggerProjects', { static: false, read: PopupTriggerDirective }) popupTriggerProjects: PopupTriggerDirective;
  @ViewChild('addUserProjectsPopup', { static: false }) addUserProjectsPopup: PopupComponent;
  @ViewChild('filterUserProjects', { static: false }) filterUserProjects: ElementRef;
  licenseUserProjectsFilterName: string = 'license-user-projects';
  filterUserProjectsSubscription: Subscription;

  @InputObservable('licenseUser') licenseUser$: Observable<LicenseUser>;
  canAssignToProjects$: Observable<boolean>;

  savingState: LoadingState<string> = new LoadingState<string>();

  canEdit$: Observable<boolean>;

  projectStatuses: ProjectStatus[] = [ProjectStatus.Active, ProjectStatus.Locked];

  constructor(
    private errorService: ErrorService,
    private permissionsService: PermissionsService,
    private projectsService: ProjectsService,
    private teamsService: TeamsService,
    private pageFilterService: PageFilterService
  ) { }

  ngOnInit() {
    // Create an observable on whether editing is allowed
    this.canEdit$ = this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageTeamsProjects);

    // Create an observable on whether this user can be assigned to projects
    this.canAssignToProjects$ = this.licenseUser$.pipe(
      map(licenseUser => licenseUser.SeatType === LicenseUserSeatType.Author)
    );
  }

  ngAfterViewInit(): void {
    this.initFilterSubscriptions();

    // set default team order
    this.teamsService.setPagedDataListOrder$(this.licenseUserTeamsListComponent.dataListId, 'Name', 'asc');
    this.teamsService.setPagedDataListOrder$(this.licenseUserTeamsAutocomplete.selectList.dataListId, 'Name', 'asc');

    // set default project list order
    if (this.licenseUserProjectsListComponent) {
      this.projectsService.setPagedDataListOrder$(this.licenseUserProjectsListComponent.dataListId, 'Name', 'asc');
      this.projectsService.setPagedDataListOrder$(this.licenseUserProjectsAutocomplete.selectList.dataListId, 'Name', 'asc');
    }
  }

  ngOnDestroy(): void {
    this.filterUserTeamsSubscription?.unsubscribe();
    this.filterUserTeamsSubscription = null;
    this.filterUserProjectsSubscription?.unsubscribe();
    this.filterUserProjectsSubscription = null;
  }

  onSaved() {
    this.saved.emit();
  }

  onTabChange(event: MatTabChangeEvent) {
    this.initFilterSubscriptions();

    switch (event.tab.textLabel) {
      case 'Teams':
        this.licenseUserTeamsListComponent.checkViewportSize();
        break;
      case 'Projects':
        this.licenseUserProjectsListComponent.checkViewportSize();
        break;
    }
  }

  initFilterSubscriptions() {
    if (!this.filterUserTeamsSubscription) {
      this.filterUserTeamsSubscription = this.licenseUserTeamsListComponent?.getFilter$(this.licenseUserTeamsFilterName).subscribe(filter => {
        if (!filter) {
          this.filterUserTeams.nativeElement.value = '';
        }
      });
    }
    if (!this.filterUserProjectsSubscription) {
      this.filterUserProjectsSubscription = this.licenseUserProjectsListComponent?.getFilter$(this.licenseUserProjectsFilterName).subscribe(filter => {
        if (!filter) {
          this.filterUserProjects.nativeElement.value = '';
        }
      });
    }
  }

  onAddUserTeamsClicked() {
    if (this.licenseUserTeamsAutocomplete.value) {
      this.savingState.update(true);

      this.teamsService.addLicenseUserToTeams$(this.licenseUser.Id, this.licenseUserTeamsAutocomplete.value as number[]).subscribe(() => {
        this.licenseUserTeamsListComponent.hardReload();
        this.licenseUserTeamsAutocomplete.close();
        this.popupTriggerTeams.close();
        this.savingState.update(false);
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'There was an error adding teams to the user.', this.errorService.getErrorMessages(error));
      });
    }
  }
  onCancelUserTeamsClicked() {
    this.savingState.update(false);
    this.addUserTeamsPopup.close();
  }
  onUserTeamsPopupClosed() {
    this.licenseUserTeamsAutocomplete.value = null;
  }
  onFilterUserTeamsKeyUp(event: any) {
    const fb = this.pageFilterService.create({
      Id: this.licenseUserTeamsFilterName,
      Type: PageFilterGroupType.Search
    });
    fb.search(['Name'], event.target.value, [
      { PropertyName: 'Name', PropertyType: PageDataType.String }
    ]);
    this.teamsService.setPagedDataListFilterByName$(this.licenseUserTeamsListComponent.dataListId, this.licenseUserTeamsFilterName, fb.value);
  }

  onAddUserProjectsClicked() {
    if (this.licenseUserProjectsAutocomplete.value) {
      this.savingState.update(true);

      this.projectsService.addLicenseUserToProjects$(this.licenseUser.Id, this.licenseUserProjectsAutocomplete.value as number[]).subscribe(() => {
        this.licenseUserProjectsListComponent.hardReload();
        this.licenseUserProjectsAutocomplete.close();
        this.popupTriggerProjects.close();
        this.savingState.update(false);
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'There was an error adding projects to the user.', this.errorService.getErrorMessages(error));
      });
    }
  }
  onCancelUserProjectsClicked() {
    this.savingState.update(false);
    this.addUserProjectsPopup.close();
  }
  onUserProjectsPopupClosed() {
    this.licenseUserProjectsAutocomplete.value = null;
  }
  onFilterUserProjectsKeyUp(event: any) {
    const fb = this.pageFilterService.create({
      Id: this.licenseUserProjectsFilterName,
      Type: PageFilterGroupType.Search
    });
    fb.search(['Name', 'Initials'], event.target.value, [
      { PropertyName: 'Name', PropertyType: PageDataType.String },
      { PropertyName: 'Initials', PropertyType: PageDataType.String }
    ]);
    this.projectsService.setPagedDataListFilterByName$(this.licenseUserProjectsListComponent.dataListId, this.licenseUserProjectsFilterName, fb.value);
  }

}
